import { type JSX } from 'react';

import { StyleSheet, Text, View } from '@react-pdf/renderer';

import { PDF_FONTS, PDF_FONT_WEIGHTS } from '../lib/pdfTokens';

const SEPARATOR = '#CBD5E1';

const styles = StyleSheet.create({
  table: {
    borderTopWidth: 0.5,
    borderTopColor: SEPARATOR,
    borderTopStyle: 'solid',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'baseline',
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: SEPARATOR,
    borderBottomStyle: 'solid',
  },
  label: {
    width: 100,
    fontSize: 8,
    fontWeight: PDF_FONT_WEIGHTS.bold,
    fontFamily: PDF_FONTS.sans,
    color: '#25252A',
    textTransform: 'uppercase',
    letterSpacing: 0.32,
    lineHeight: 1.35,
  },
  value: {
    flex: 1,
    fontSize: 10,
    fontWeight: 300,
    fontFamily: PDF_FONTS.sans,
    color: '#25252A',
    lineHeight: 1.35,
  },
  singleLine: {
    maxLines: 1,
    textOverflow: 'ellipsis',
  },
  extra: {
    fontSize: 10,
    fontWeight: 300,
    fontFamily: PDF_FONTS.sans,
    color: '#5E5E69',
    lineHeight: 1.35,
    textAlign: 'right',
  },
});

export type PdfDataTableRow = {
  label: string;
  value: string;
  extra?: string;
  /** Truncates the value with an ellipsis instead of wrapping to a second line. */
  singleLine?: boolean;
};

type PdfDataTableProps = {
  rows: PdfDataTableRow[];
  extraWidth?: number;
  labelWidth?: number;
  valueWidth?: number;
};

export function PdfDataTable({ rows, extraWidth = 80, labelWidth, valueWidth }: PdfDataTableProps): JSX.Element {
  const labelStyle = labelWidth !== undefined ? [styles.label, { width: labelWidth }] : styles.label;
  const valueStyle = valueWidth !== undefined ? [styles.value, { flex: undefined, width: valueWidth }] : styles.value;
  const hasExtra = rows.some((row) => row.extra !== undefined);
  return (
    <View style={styles.table}>
      {rows.map((row) => (
        <View
          key={row.label}
          style={styles.row}
          wrap={false}>
          <Text style={labelStyle}>{row.label}</Text>
          <Text style={row.singleLine ? [valueStyle, styles.singleLine].flat() : valueStyle}>{row.value}</Text>
          {hasExtra ? <Text style={[styles.extra, { width: extraWidth }]}>{row.extra}</Text> : null}
        </View>
      ))}
    </View>
  );
}
